import * as React from 'react';
import {
  ModalWindow,
  SwitchDialogWindows,
  Grid,
  ISpruceFieldArrayProps,
  ISpruceFieldProps,
  SpruceArrayField,
  SpruceField,
  Button,
} from 'ui-toolkit';
import { IStandardDocumentData } from 'types/order/documents/shared/interfaces';
import { DocumentModalTitle } from './DocumentModalTitle';
import { categoryForDisplay } from 'types/order/documents/shared/utilities';
import { DocumentData } from './DocumentData';
import { ErrorMessage, Form, Formik } from 'formik';
import { useState } from 'react';
import { uploadDocumentValidation } from './uploadDocumentValidation';
import { DocumentCategoriesByName, IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';

interface IEditDocumentModalProps {
  document?: IStandardDocumentData;
  hasSaveError: boolean;
  isComplete: boolean;
  isLoading: boolean;
  onEdit: (document: IStandardDocumentData) => Promise<any>;
  onClose: () => void;
  standardDocumentCategories: IDocumentCategory[];
  documentCategoriesByName: DocumentCategoriesByName;
}

export const EditDocumentModalController: React.FC<IEditDocumentModalProps> = ({
  document,
  hasSaveError,
  isComplete,
  isLoading,
  onClose,
  onEdit,
  standardDocumentCategories,
  documentCategoriesByName,
}) => {
  const [showInputs, setShowInput] = useState(false);
  return (
    <SwitchDialogWindows
      isLoading={isLoading}
      isCompleted={isComplete}
      isError={hasSaveError}
      loadingTitle={isLoading ? 'Loading file...' : 'Saving your file...'}
      successTitle="Your file has been saved."
      errorTitle="Your file could not be saved."
      onCloseClick={onClose}
    >
      <ModalWindow
        contentJustifyContent={'start'}
        contentTextAlign={'left'}
        minHeight={400}
        onClose={onClose}
        title={
          document ? (
            <DocumentModalTitle superText={document.fileName}>
              {showInputs ? 'Edit ' : null}
              {categoryForDisplay(standardDocumentCategories, document.category)} Details
            </DocumentModalTitle>
          ) : null
        }
        width={580}
      >
        <Formik
          initialValues={{
            files: document ? [{ ...document, file: {} }] : [],
          }}
          validationSchema={uploadDocumentValidation(documentCategoriesByName)}
          isInitialValid={false}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            try {
              await onEdit(values.files[0]);
            } catch (e: any) {}
          }}
          onReset={onClose}
        >
          {({ errors, touched, values, isValid, setSubmitting, handleSubmit }) => {
            const error =
              errors.files && errors.files[0] && (errors.files[0] as any).metadata
                ? (errors.files[0] as any).metadata.good_through_date
                : null;
            return (
              <Form onSubmit={handleSubmit}>
                <Grid>
                  <SpruceArrayField name={'files'}>
                    {({ field, formik }: ISpruceFieldArrayProps) => (
                      <>
                        {field.value && formik.values.files[0] ? (
                          <SpruceField name={`files[0].metadata`}>
                            {({ field }: ISpruceFieldProps) => (
                              <DocumentData
                                document={document}
                                formValue={field.value}
                                errorText={error}
                                onChange={field.onChange}
                                showInputs={showInputs}
                                documentCategoriesByName={documentCategoriesByName}
                              />
                            )}
                          </SpruceField>
                        ) : (
                          <ErrorMessage name={'Your file could not be edited.'} />
                        )}
                      </>
                    )}
                  </SpruceArrayField>
                  {showInputs ? (
                    <Grid templateColumns={'min-content min-content'}>
                      <Button intent={'primary'} type={'submit'} disabled={!isValid}>
                        Update Details
                      </Button>
                      <Button intent={'secondary'} type={'reset'}>
                        Close
                      </Button>
                    </Grid>
                  ) : (
                    <Grid templateColumns={'min-content'}>
                      <Button intent={'secondary'} type={'button'} onClick={() => setShowInput(true)}>
                        Edit Details
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};
